import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Check, X, Edit3, Save, Truck, PackageCheck, FileDown, Mail, Download, RefreshCw, Trash2, Copy, GitMerge, Send } from "lucide-react";
import Layout from "../components/Layout";
import { api, API, auth } from "../lib/api";

// Volume-K tier ladder (mirrors pricing.VOLUME_TIER_LADDER in the backend).
// Operator-facing: every K decimal has a whole-number tier 0..6.
const VOLUME_TIER_TO_K = { 0: 0.01, 1: 0.10, 2: 0.50, 3: 0.65, 4: 0.80, 5: 1.00, 6: 1.20 };
const MIN_VOLUME_TIER = 0;
const MAX_VOLUME_TIER = 6;
const kToTier = (k) => {
  if (k == null) return 2;
  const num = Number(k);
  let bestTier = 2;
  let bestDiff = Infinity;
  for (const [t, kk] of Object.entries(VOLUME_TIER_TO_K)) {
    const d = Math.abs(kk - num);
    if (d < bestDiff) { bestDiff = d; bestTier = Number(t); }
  }
  return bestTier;
};
const tierToK = (t) => {
  const clamped = Math.max(MIN_VOLUME_TIER, Math.min(MAX_VOLUME_TIER, Number(t)));
  return VOLUME_TIER_TO_K[clamped];
};

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [confirmDone, setConfirmDone] = useState(false);
  const [confirmAccept, setConfirmAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(""); // "download" | "email" | ""
  const [exportMsg, setExportMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/quotes/${id}`);
      setQuote(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // While the background enrichment is still running, poll the quote every
  // 5s so the admin sees items get populated in real time without manually
  // refreshing the page. We stop polling as soon as the backend flips
  // enrichment_status to "done" (or there's no flag at all on older quotes).
  useEffect(() => {
    if (quote?.enrichment_status !== "pending") return;
    const t = setInterval(() => { load(); }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [quote?.enrichment_status]);

  const valKey = quote?.quote_type === "cash" ? "cash_value" : "trade_value";
  const valLabel = quote?.quote_type === "cash" ? "Cash Out Value" : "Trade-In Credit";

  const startEdit = () => {
    setEditing(true);
    const seed = {};
    quote.items.forEach((it) => {
      seed[it.id] = {
        condition: it.condition,
        condition_constant: it.condition_constant,
        // Edit on the whole-number tier (0..6) — much easier than typing decimals.
        sales_volume_tier: it.sales_volume_tier_adjusted ?? kToTier(it.sales_volume_constant),
        base_value: it.base_value,
      };
    });
    setEdits(seed);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEdits({});
  };

  const updateField = (itemId, key, val) => {
    setEdits((p) => ({ ...p, [itemId]: { ...p[itemId], [key]: val } }));
  };

  // Per-item barcode lookup (used for items whose name-based PC search failed).
  const [barcodeInputs, setBarcodeInputs] = useState({});
  const [barcodeBusy, setBarcodeBusy] = useState(null); // item id currently looking up
  const [barcodeErr, setBarcodeErr] = useState({});

  const lookupItemByBarcode = async (itemId) => {
    const barcode = (barcodeInputs[itemId] || "").trim();
    if (!barcode) return;
    setBarcodeBusy(itemId);
    setBarcodeErr((p) => ({ ...p, [itemId]: "" }));
    try {
      const r = await api.post(`/quotes/${id}/items/${itemId}/barcode`, { barcode });
      setQuote(r.data);
      setBarcodeInputs((p) => ({ ...p, [itemId]: "" }));
    } catch (e) {
      setBarcodeErr((p) => ({ ...p, [itemId]: e.response?.data?.detail || "Lookup failed" }));
    } finally {
      setBarcodeBusy(null);
    }
  };

  // Per-item PC URL override (used when fuzzy/auto lookup matched the wrong game).
  const [pcUrlOpen, setPcUrlOpen] = useState({});       // {itemId: bool}
  const [pcUrlInputs, setPcUrlInputs] = useState({});   // {itemId: string}
  const [pcUrlBusy, setPcUrlBusy] = useState(null);
  const [pcUrlErr, setPcUrlErr] = useState({});

  const overrideItemPcUrl = async (itemId) => {
    const url = (pcUrlInputs[itemId] || "").trim();
    if (!url) return;
    setPcUrlBusy(itemId);
    setPcUrlErr((p) => ({ ...p, [itemId]: "" }));
    try {
      const r = await api.post(`/quotes/${id}/items/${itemId}/pc-url`, { pc_url: url });
      setQuote(r.data);
      setPcUrlOpen((p) => ({ ...p, [itemId]: false }));
      setPcUrlInputs((p) => ({ ...p, [itemId]: "" }));
    } catch (e) {
      setPcUrlErr((p) => ({ ...p, [itemId]: e.response?.data?.detail || "Failed" }));
    } finally {
      setPcUrlBusy(null);
    }
  };

  const removeItem = async (itemId, title) => {
    if (!window.confirm(`Remove "${title}" from this quote?`)) return;
    try {
      const r = await api.delete(`/quotes/${id}/items/${itemId}`);
      setQuote(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to remove item");
    }
  };

  // Admin-added "negotiation bonus" — manual top-up applied to the quote total.
  const [adminAddedDraft, setAdminAddedDraft] = useState("");
  const [adminAddedBusy, setAdminAddedBusy] = useState(false);
  const [adminAddedErr, setAdminAddedErr] = useState("");
  const saveAdminAddedValue = async () => {
    setAdminAddedBusy(true);
    setAdminAddedErr("");
    try {
      const raw = adminAddedDraft.trim();
      const val = raw === "" ? null : Number(raw);
      if (raw !== "" && (!Number.isFinite(val) || val < 0)) {
        setAdminAddedErr("Enter a positive number or leave blank to clear.");
        setAdminAddedBusy(false);
        return;
      }
      const r = await api.patch(`/quotes/${id}/admin-added-value`, { admin_added_value: val });
      setQuote(r.data);
      setAdminAddedDraft("");
    } catch (e) {
      setAdminAddedErr(e.response?.data?.detail || "Save failed");
    } finally {
      setAdminAddedBusy(false);
    }
  };

  const [duplicating, setDuplicating] = useState(false);
  const duplicateQuote = async () => {
    if (!window.confirm("Duplicate this quote? You'll be taken to the new copy so you can edit it freely.")) return;
    setDuplicating(true);
    try {
      const r = await api.post(`/quotes/${id}/duplicate`);
      navigate(`/pos/quotes/${r.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Duplicate failed");
    } finally {
      setDuplicating(false);
    }
  };

  // Combine sibling quotes from the same customer into this one.
  const [combineOpen, setCombineOpen] = useState(false);
  const [siblings, setSiblings] = useState([]);
  const [siblingsLoading, setSiblingsLoading] = useState(false);
  const [siblingErr, setSiblingErr] = useState("");
  const [pickedSiblings, setPickedSiblings] = useState(new Set());
  const [combining, setCombining] = useState(false);

  const openCombine = async () => {
    setCombineOpen(true);
    setSiblings([]);
    setPickedSiblings(new Set());
    setSiblingErr("");
    setSiblingsLoading(true);
    try {
      const r = await api.get(`/quotes/${id}/siblings`);
      setSiblings(r.data);
    } catch (e) {
      setSiblingErr(e.response?.data?.detail || "Failed to load other quotes");
    } finally {
      setSiblingsLoading(false);
    }
  };
  const togglePicked = (qid) => {
    setPickedSiblings((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid); else next.add(qid);
      return next;
    });
  };
  const confirmCombine = async () => {
    if (pickedSiblings.size === 0) return;
    setCombining(true);
    setSiblingErr("");
    try {
      const r = await api.post(`/quotes/${id}/combine`, { quote_ids: Array.from(pickedSiblings) });
      setQuote(r.data);
      setCombineOpen(false);
    } catch (e) {
      setSiblingErr(e.response?.data?.detail || "Combine failed");
    } finally {
      setCombining(false);
    }
  };

  const saveAndSend = async () => {
    setBusy(true);
    setError("");
    try {
      for (const itemId of Object.keys(edits)) {
        const patch = edits[itemId];
        await api.patch(`/quotes/${id}/items/${itemId}`, {
          condition: patch.condition,
          condition_constant: Number(patch.condition_constant),
          sales_volume_constant: tierToK(patch.sales_volume_tier),
          base_value: patch.base_value === "" || patch.base_value == null ? null : Number(patch.base_value),
        });
      }
      // Done editing now only persists the edits — the owner emails the
      // customer manually via the separate "Email Update" button once the
      // quote looks right.
      setEditing(false);
      setConfirmDone(false);
      setEdits({});
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  // Email the updated quote to the customer. Enabled only when there are
  // unsent edits on the quote (server-tracked); after a successful send the
  // backend clears has_unsent_edits, so the button greys out until the next
  // edit.
  const [sendingUpdate, setSendingUpdate] = useState(false);
  const sendUpdateEmail = async () => {
    if (!window.confirm("Email the updated quote to the customer?")) return;
    setSendingUpdate(true);
    setError("");
    try {
      await api.post(`/quotes/${id}/send-update`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Email failed");
    } finally {
      setSendingUpdate(false);
    }
  };

  const decline = async () => {
    if (!window.confirm("Decline & delete this quote?")) return;
    await api.post(`/quotes/${id}/decline`);
    navigate("/pos/quotes");
  };

  const requestDropoff = async () => {
    if (!window.confirm("Email the customer drop-off instructions and move this quote to Pending?")) return;
    setBusy(true);
    try {
      await api.post(`/quotes/${id}/request-dropoff`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to request drop-off");
    } finally {
      setBusy(false);
    }
  };

  const readyForPickup = async () => {
    if (!window.confirm("Email the customer that their items are ready for pickup (24h window) and remove this quote?")) return;
    setBusy(true);
    try {
      await api.post(`/quotes/${id}/ready-for-pickup`);
      navigate("/pos/quotes");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to send pickup email");
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/quotes/${id}/accept`);
      navigate("/pos/quotes");
    } catch (e) {
      setError(e.response?.data?.detail || "Accept failed");
    } finally {
      setBusy(false);
      setConfirmAccept(false);
    }
  };

  const downloadPdf = async () => {
    setExporting("download");
    setExportMsg("");
    try {
      // Use raw fetch so we can stream the binary PDF + read the filename
      // out of Content-Disposition rather than guessing.
      const res = await fetch(`${API}/quotes/${id}/pdf`, {
        headers: { "X-Admin-Token": auth.token },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m?.[1] || `AB_Microplay_Quote_${id.slice(0, 8)}.pdf`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (e) {
      setExportMsg(`Download failed: ${e.message || e}`);
    } finally {
      setExporting("");
    }
  };

  const emailPdf = async () => {
    setExporting("email");
    setExportMsg("");
    try {
      const r = await api.post(`/quotes/${id}/pdf/email`);
      setExportMsg(`Emailed to ${r.data.to}.`);
    } catch (e) {
      setExportMsg(`Email failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setExporting("");
    }
  };

  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState("");
  const refreshUnresolved = async () => {
    setRefreshing(true);
    setRefreshMsg("");
    try {
      const r = await api.post(`/quotes/${id}/refresh-unresolved`);
      const queued = r.data?.queued ?? 0;
      const started = r.data?.started ?? false;
      if (!started) {
        setRefreshMsg("Nothing to refresh — every item already has a value.");
      } else {
        setRefreshMsg(`Re-running PriceCharting on ${queued} item${queued === 1 ? "" : "s"} — values will update below as they land.`);
      }
      await load();
    } catch (e) {
      setRefreshMsg(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const [updatingAll, setUpdatingAll] = useState(false);
  const updateAllPrices = async () => {
    if (!window.confirm(
      "Update ALL item prices from current PriceCharting? Items you manually edited will be left alone. This will run in the background — values populate below as they refresh."
    )) return;
    setUpdatingAll(true);
    setRefreshMsg("");
    try {
      const r = await api.post(`/quotes/${id}/refresh-all`);
      const queued = r.data?.queued ?? 0;
      const skipped = r.data?.skipped_manual ?? 0;
      const started = r.data?.started ?? false;
      if (!started) {
        setRefreshMsg("Nothing to update — every item is manually overridden.");
      } else {
        const parts = [`Re-pricing ${queued} item${queued === 1 ? "" : "s"} from PriceCharting`];
        if (skipped) parts.push(`${skipped} manual override${skipped === 1 ? "" : "s"} preserved`);
        setRefreshMsg(parts.join(" · ") + " — updates will land below shortly.");
      }
      await load();
    } catch (e) {
      setRefreshMsg(e.response?.data?.detail || "Update failed");
    } finally {
      setUpdatingAll(false);
    }
  };

  if (loading) return (
    <Layout><div className="max-w-4xl mx-auto px-6 py-16"><Loader2 className="animate-spin" /></div></Layout>
  );
  if (!quote) return (
    <Layout><div className="max-w-4xl mx-auto px-6 py-16">{error}</div></Layout>
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/pos/quotes")} className="label-eyebrow mb-6 inline-block" data-testid="quote-detail-back">← Back to Quotes</button>

        <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
          <div>
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>{quote.quote_type.toUpperCase()} QUOTE</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black">{quote.customer_name}</h1>
            <div className="text-sm mt-1" style={{ color: "var(--ab-text-muted)" }}>{quote.customer_email} &middot; {quote.customer_phone}</div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow mb-1">Status</div>
            <div className="font-mono uppercase tracking-widest text-xs px-3 py-1 inline-block" style={{ background: "var(--ab-surface-2)", border: "1px solid var(--ab-border)" }} data-testid="quote-detail-status">
              {quote.status}
            </div>
          </div>
        </div>

        <div className="surface mb-6">
          <div className="px-5 py-3 border-b grid grid-cols-12 gap-2 label-eyebrow" style={{ borderColor: "var(--ab-border)" }}>
            <div className="col-span-3">Game</div>
            <div className="col-span-2">Condition</div>
            <div className="col-span-2 text-right">PC Base</div>
            <div className="col-span-2 text-right">Cond. K</div>
            <div className="col-span-1 text-right">Vol. Tier</div>
            <div className="col-span-2 text-right">{valLabel}</div>
          </div>
          {quote.items.map((it) => {
            const e = edits[it.id] || {};
            const val = it[valKey];
            const max = quote.quote_type === "cash" ? it.cash_max : it.trade_max;
            const needsLookup = it.base_value == null;
            return (
              <div key={it.id} className="border-b" style={{ borderColor: "var(--ab-border)" }} data-testid={`quote-detail-item-${it.id}`}>
                <div className="px-5 py-4 grid grid-cols-12 gap-2 items-center">
                <div className="col-span-3">
                  <div className="font-bold truncate">{it.title}</div>
                  <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{it.console} &middot; {it.components}</div>
                  <div className="text-xs mt-0.5 flex gap-2 items-center">
                    {it.pc_url && <a href={it.pc_url} target="_blank" rel="noreferrer" className="underline" style={{ color: "var(--ab-primary)" }}>PC ↗</a>}
                    {!editing && (
                      <button
                        type="button"
                        onClick={() => setPcUrlOpen((p) => ({ ...p, [it.id]: !p[it.id] }))}
                        className="underline"
                        style={{ color: "var(--ab-text-dim)" }}
                        data-testid={`quote-item-edit-pc-url-toggle-${it.id}`}
                      >
                        {pcUrlOpen[it.id] ? "cancel" : "edit URL"}
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  {editing ? (
                    <select className="input-terminal" style={{ padding: "6px 8px", fontSize: 12 }} value={e.condition} onChange={(ev) => updateField(it.id, "condition", ev.target.value)} data-testid={`edit-condition-${it.id}`}>
                      {["New","Like New","Excellent","Acceptable","Poor","Not Working"].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  ) : it.condition}
                </div>
                <div className="col-span-2 text-right font-mono">
                  {editing ? (
                    <input type="number" step="0.01" className="input-terminal text-right" style={{ padding: "6px 8px", fontSize: 12 }} value={e.base_value ?? ""} onChange={(ev) => updateField(it.id, "base_value", ev.target.value)} data-testid={`edit-base-${it.id}`} />
                  ) : (it.base_value != null ? `$${it.base_value.toFixed(2)}` : <span style={{ color: "var(--ab-warning)" }}>—</span>)}
                  <div className="text-[10px]" style={{ color: "var(--ab-text-dim)" }}>max ${max?.toFixed?.(2) ?? "—"}</div>
                </div>
                <div className="col-span-2 text-right font-mono">
                  {editing ? (
                    <input type="number" step="0.01" min="0" max="2" className="input-terminal text-right" style={{ padding: "6px 8px", fontSize: 12 }} value={e.condition_constant} onChange={(ev) => updateField(it.id, "condition_constant", ev.target.value)} data-testid={`edit-cond-k-${it.id}`} />
                  ) : it.condition_constant?.toFixed?.(2)}
                </div>
                <div className="col-span-1 text-right font-mono">
                  {editing ? (
                    <input
                      type="number"
                      step="1"
                      min={MIN_VOLUME_TIER}
                      max={MAX_VOLUME_TIER}
                      className="input-terminal text-right"
                      style={{ padding: "6px 8px", fontSize: 12 }}
                      value={e.sales_volume_tier ?? ""}
                      onChange={(ev) => updateField(it.id, "sales_volume_tier", ev.target.value)}
                      data-testid={`edit-vol-tier-${it.id}`}
                    />
                  ) : (() => {
                    const adj = it.sales_volume_tier_adjusted ?? kToTier(it.sales_volume_constant);
                    const baseT = it.sales_volume_tier_base;
                    const invCount = it.inventory_count_at_quote;
                    const penalised = baseT != null && adj != null && baseT !== adj;
                    return (
                      <span title={penalised ? `Base tier ${baseT}, ${invCount} in stock → adjusted to ${adj}` : undefined}>
                        {adj ?? "—"}
                        {penalised && (
                          <div className="text-[10px]" style={{ color: "var(--ab-warning)" }} data-testid={`vol-tier-penalty-${it.id}`}>
                            was {baseT} · {invCount} in stock
                          </div>
                        )}
                      </span>
                    );
                  })()}
                </div>
                <div className="col-span-2 text-right font-mono font-bold" style={{ color: "var(--ab-primary)" }}>
                  <div className="flex justify-end items-center gap-2">
                    <span>{val != null ? `$${val.toFixed(2)}` : "—"}</span>
                    {!editing && quote.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(it.id, it.title)}
                        title="Remove this item from the quote"
                        className="inline-flex items-center justify-center"
                        style={{ width: 22, height: 22, border: "1px solid var(--ab-danger)", color: "var(--ab-danger)", background: "rgba(239,68,68,0.08)" }}
                        data-testid={`quote-item-remove-${it.id}`}
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                </div>
                </div>
                {pcUrlOpen[it.id] && !editing && (
                  <div className="px-5 pb-4 pt-0 flex flex-wrap items-center gap-2" style={{ background: "rgba(37,99,235,0.06)" }} data-testid={`quote-item-pc-url-row-${it.id}`}>
                    <span className="label-eyebrow" style={{ color: "var(--ab-primary)" }}>Override PC URL</span>
                    <input
                      className="input-terminal flex-1 min-w-[260px]"
                      style={{ padding: "6px 10px", fontSize: 12 }}
                      placeholder="https://www.pricecharting.com/game/..."
                      value={pcUrlInputs[it.id] || ""}
                      onChange={(ev) => setPcUrlInputs((p) => ({ ...p, [it.id]: ev.target.value }))}
                      onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); overrideItemPcUrl(it.id); } }}
                      data-testid={`quote-item-pc-url-input-${it.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => overrideItemPcUrl(it.id)}
                      disabled={pcUrlBusy === it.id || !(pcUrlInputs[it.id] || "").trim()}
                      className="btn-primary inline-flex items-center gap-2"
                      style={{ padding: "6px 12px", fontSize: 11 }}
                      data-testid={`quote-item-pc-url-apply-${it.id}`}
                    >
                      {pcUrlBusy === it.id ? <Loader2 size={12} className="animate-spin" /> : null} Apply
                    </button>
                    {pcUrlErr[it.id] && (
                      <span className="text-xs" style={{ color: "var(--ab-danger)" }} data-testid={`quote-item-pc-url-err-${it.id}`}>
                        {pcUrlErr[it.id]}
                      </span>
                    )}
                  </div>
                )}
                {needsLookup && !editing && (
                  <div className="px-5 pb-4 pt-0 flex flex-wrap items-center gap-2" style={{ background: "rgba(245,158,11,0.06)" }} data-testid={`quote-item-barcode-row-${it.id}`}>
                    <span className="label-eyebrow" style={{ color: "var(--ab-warning)" }}>No PC match — try barcode</span>
                    <input
                      className="input-terminal flex-1 min-w-[180px]"
                      style={{ padding: "6px 10px", fontSize: 12 }}
                      placeholder="e.g. 045496870010"
                      value={barcodeInputs[it.id] || ""}
                      onChange={(ev) => setBarcodeInputs((p) => ({ ...p, [it.id]: ev.target.value }))}
                      onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); lookupItemByBarcode(it.id); } }}
                      data-testid={`quote-item-barcode-input-${it.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => lookupItemByBarcode(it.id)}
                      disabled={barcodeBusy === it.id || !(barcodeInputs[it.id] || "").trim()}
                      className="btn-secondary inline-flex items-center gap-2"
                      style={{ padding: "6px 12px", fontSize: 11 }}
                      data-testid={`quote-item-barcode-lookup-${it.id}`}
                    >
                      {barcodeBusy === it.id ? <Loader2 size={12} className="animate-spin" /> : null} Look up
                    </button>
                    {barcodeErr[it.id] && (
                      <span className="text-xs" style={{ color: "var(--ab-danger)" }} data-testid={`quote-item-barcode-err-${it.id}`}>
                        {barcodeErr[it.id]}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {/* Admin-added negotiation bonus — manual top-up on top of items. */}
          {(quote.admin_added_value || editing) && (
            <div className="px-5 py-4 border-b grid grid-cols-12 gap-2 items-center" style={{ borderColor: "var(--ab-border)", background: "rgba(34,197,94,0.04)" }} data-testid="quote-admin-added-row">
              <div className="col-span-7">
                <div className="font-bold" style={{ color: "var(--ab-primary)" }}>Admin Added Value</div>
                <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>Manual negotiation bonus — added on top of item values</div>
              </div>
              <div className="col-span-3">
                {editing ? (
                  <div className="flex items-center gap-2">
                    <span style={{ color: "var(--ab-text-dim)" }}>$</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={String(quote.admin_added_value ?? 0)}
                      className="input-terminal"
                      style={{ padding: "6px 8px", fontSize: 12, width: 100 }}
                      value={adminAddedDraft}
                      onChange={(e) => setAdminAddedDraft(e.target.value)}
                      data-testid="quote-admin-added-input"
                    />
                    <button
                      type="button"
                      onClick={saveAdminAddedValue}
                      disabled={adminAddedBusy}
                      className="btn-secondary"
                      style={{ padding: "4px 10px", fontSize: 11 }}
                      data-testid="quote-admin-added-apply"
                    >
                      {adminAddedBusy ? <Loader2 size={12} className="animate-spin" /> : "Apply"}
                    </button>
                  </div>
                ) : null}
                {adminAddedErr && (
                  <div className="text-[11px] mt-1" style={{ color: "var(--ab-danger)" }} data-testid="quote-admin-added-err">{adminAddedErr}</div>
                )}
              </div>
              <div className="col-span-2 text-right font-mono font-bold" style={{ color: "var(--ab-primary)" }} data-testid="quote-admin-added-display">
                {quote.admin_added_value ? `+$${Number(quote.admin_added_value).toFixed(2)}` : "—"}
              </div>
            </div>
          )}

          <div className="px-5 py-4 flex justify-between" style={{ background: "var(--ab-surface-2)" }}>
            <span className="label-eyebrow">Total {valLabel}</span>
            <span className="font-mono font-bold text-2xl" style={{ color: "var(--ab-primary)" }} data-testid="quote-detail-total">
              ${(quote.items.reduce((s, it) => s + (it[valKey] || 0), 0) + Number(quote.admin_added_value || 0)).toFixed(2)}
            </span>
          </div>
          {/* Admin-only payout summary — totals PC retail value across all
              items and shows what % of that the customer is being paid. */}
          {(() => {
            const totalPc = quote.items.reduce((s, it) => s + (Number(it.base_value) || 0), 0);
            const totalPayout = quote.items.reduce((s, it) => s + (Number(it[valKey]) || 0), 0) + Number(quote.admin_added_value || 0);
            const pct = totalPc > 0 ? (totalPayout / totalPc) * 100 : null;
            return (
              <div className="px-5 py-3 flex flex-wrap items-center justify-between gap-4 text-xs" style={{ background: "var(--ab-surface)", borderTop: "1px solid var(--ab-border)" }} data-testid="quote-admin-payout-summary">
                <span className="label-eyebrow" style={{ color: "var(--ab-text-dim)" }}>Admin-only summary</span>
                <div className="flex flex-wrap items-center gap-5 font-mono">
                  <span data-testid="quote-total-pc-value">
                    <span style={{ color: "var(--ab-text-dim)" }}>PC retail </span>
                    <b style={{ color: "var(--ab-text)" }}>${totalPc.toFixed(2)}</b>
                  </span>
                  <span data-testid="quote-payout-percent">
                    <span style={{ color: "var(--ab-text-dim)" }}>Payout </span>
                    <b style={{ color: pct != null && pct <= 50 ? "var(--ab-primary)" : "var(--ab-warning)" }}>
                      {pct != null ? `${pct.toFixed(1)}%` : "—"}
                    </b>
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        {error && <div className="mb-4 px-4 py-3 text-sm" style={{ background: "rgba(239,68,68,0.1)", color: "var(--ab-danger)", border: "1px solid var(--ab-danger)" }}>{error}</div>}

        {quote.has_unsent_edits && quote.status !== "approved" && (
          <div className="mb-4 px-4 py-3 text-sm flex items-center gap-3" style={{ background: "rgba(245,158,11,0.08)", color: "var(--ab-warning)", border: "1px solid var(--ab-warning)" }} data-testid="quote-unsent-edits-banner">
            <Send size={14} />
            <span><b>Unsent edits.</b> Use <b>Email Update</b> when ready to send these to the customer. Accept/Decline/Drop-off are locked until the customer responds.</span>
          </div>
        )}
        {quote.status === "pending" && !quote.has_unsent_edits && (
          <div className="mb-4 px-4 py-3 text-sm flex items-center gap-3" style={{ background: "rgba(37,99,235,0.08)", color: "var(--ab-primary)", border: "1px solid var(--ab-primary)" }} data-testid="quote-awaiting-customer-banner">
            <Loader2 size={14} className="animate-spin" />
            <span><b>Waiting on the customer.</b> Update email is out. Accept/Decline/Drop-off will unlock once the customer accepts in their email.</span>
          </div>
        )}
        {refreshMsg && (
          <div className="mb-4 px-4 py-2 text-xs" style={{ background: "var(--ab-surface-2)", border: "1px solid var(--ab-border)" }} data-testid="quote-refresh-msg">
            {refreshMsg}
          </div>
        )}
        {quote.enrichment_status === "pending" && (
          <div className="mb-4 px-4 py-3 text-sm flex items-center gap-3" style={{ background: "rgba(245,158,11,0.08)", color: "var(--ab-warning)", border: "1px solid var(--ab-warning)" }} data-testid="quote-enrichment-banner">
            <Loader2 size={14} className="animate-spin" />
            <span><b>Looking up prices…</b> PriceCharting data is still loading for this quote. Items will populate as values come in. This page auto-refreshes every few seconds.</span>
          </div>
        )}
        {quote.awaiting_pickup && (
          <div className="mb-4 px-4 py-3 text-sm" style={{ background: "rgba(245,158,11,0.08)", color: "var(--ab-warning)", border: "1px solid var(--ab-warning)" }} data-testid="quote-awaiting-pickup-banner">
            <b>Awaiting Pickup.</b> Customer declined after drop-off — their items are on-site. Click <b>Ready for Pickup</b> when staged for the customer to retrieve.
          </div>
        )}
        {(quote.status === "approved" || quote.status === "accepted_by_customer") && (
          <div className="mb-4 px-4 py-3 text-sm flex items-center gap-3" style={{ background: "rgba(34,197,94,0.08)", color: "#4ADE80", border: "1px solid #22C55E" }} data-testid="quote-approved-banner">
            <span className="status-approved" />
            <span><b>Approved.</b> The customer has accepted this quote. Click <b>Accept</b> to complete the transaction and add the items to inventory.</span>
          </div>
        )}
        {!quote.awaiting_pickup && quote.dropoff_requested && (
          <div className="mb-4 px-4 py-3 text-sm" style={{ background: "rgba(37,99,235,0.08)", color: "var(--ab-primary)", border: "1px solid var(--ab-primary)" }} data-testid="quote-dropoff-banner">
            <b>Drop-Off Requested.</b> Customer has been emailed instructions for {`435 William Avenue, Beausejour, Manitoba`}.
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {!editing ? (
            <>
              {(() => {
                // Gating logic:
                //  • Approved by customer ⇒ Edit & Email-Update locked (no more changes).
                //  • Has unsent edits ⇒ Accept/Decline/Drop-off locked until the
                //    customer responds; Email-Update enabled to send the latest.
                const isApproved = quote.status === "approved" || quote.status === "accepted_by_customer";
                const hasUnsent = !!quote.has_unsent_edits;
                // Lock the customer-final actions in two scenarios:
                //  1) Owner is mid-edit (hasUnsent) — must email customer first.
                //  2) Quote is in 'pending' (email already sent) — waiting on
                //     the customer to Accept. Owner can only proceed once the
                //     customer's response flips the status to 'approved'.
                const lockActions = (hasUnsent || quote.status === "pending") && !isApproved;
                const lockEdit = isApproved;
                const lockEmailUpdate = !hasUnsent || isApproved;
                return (
                  <>
                    <button onClick={() => setConfirmAccept(true)} disabled={lockActions} className="btn-primary inline-flex items-center gap-2" data-testid="quote-accept-btn"><Check size={14} /> Accept</button>
                    {quote.awaiting_pickup ? (
                      <button onClick={readyForPickup} disabled={busy || lockActions} className="btn-danger inline-flex items-center gap-2" data-testid="quote-ready-pickup-btn"><PackageCheck size={14} /> Ready for Pickup</button>
                    ) : (
                      <button onClick={decline} disabled={lockActions} className="btn-danger inline-flex items-center gap-2" data-testid="quote-decline-btn"><X size={14} /> Decline</button>
                    )}
                    {!quote.dropoff_requested && !quote.awaiting_pickup && (
                      <button onClick={requestDropoff} disabled={busy || lockActions} className="btn-secondary inline-flex items-center gap-2" style={{ borderColor: "var(--ab-primary)", color: "var(--ab-primary)" }} data-testid="quote-request-dropoff-btn"><Truck size={14} /> Request Drop-Off</button>
                    )}
                    <button onClick={startEdit} disabled={lockEdit} className="btn-secondary inline-flex items-center gap-2" data-testid="quote-edit-btn"><Edit3 size={14} /> Edit</button>
                    <button
                      onClick={sendUpdateEmail}
                      disabled={lockEmailUpdate || sendingUpdate}
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid="quote-email-update-btn"
                      title={lockEmailUpdate ? (isApproved ? "Customer already approved — no further updates needed" : "No edits to email") : "Email the updated quote to the customer"}
                    >
                      {sendingUpdate ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Email Update
                    </button>
                  </>
                );
              })()}
              <button onClick={refreshUnresolved} disabled={refreshing} className="btn-secondary inline-flex items-center gap-2" data-testid="quote-refresh-btn">
                {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
              </button>
              <button onClick={updateAllPrices} disabled={updatingAll} className="btn-secondary inline-flex items-center gap-2" data-testid="quote-update-all-btn" title="Re-fetch current PriceCharting values for every item (skips manual overrides)">
                {updatingAll ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Update
              </button>
              <button onClick={duplicateQuote} disabled={duplicating} className="btn-secondary inline-flex items-center gap-2" data-testid="quote-duplicate-btn">
                {duplicating ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Duplicate
              </button>
              <button onClick={openCombine} className="btn-secondary inline-flex items-center gap-2" data-testid="quote-combine-btn"><GitMerge size={14} /> Combine</button>
              <button onClick={() => { setExportOpen(true); setExportMsg(""); }} className="btn-secondary inline-flex items-center gap-2" data-testid="quote-export-pdf-btn"><FileDown size={14} /> Export PDF</button>
            </>
          ) : (
            <>
              <button onClick={() => setConfirmDone(true)} className="btn-primary inline-flex items-center gap-2" data-testid="quote-done-editing-btn"><Save size={14} /> Done Editing</button>
              <button onClick={cancelEdit} className="btn-secondary" data-testid="quote-cancel-edit-btn">Cancel</button>
              <button onClick={() => { setExportOpen(true); setExportMsg(""); }} className="btn-secondary inline-flex items-center gap-2" data-testid="quote-export-pdf-btn-editing"><FileDown size={14} /> Export PDF</button>
            </>
          )}
        </div>

        {/* Modal: Done editing? */}
        {confirmDone && (
          <Modal onClose={() => setConfirmDone(false)}>
            <h3 className="font-display text-2xl font-bold mb-3">Save your edits?</h3>
            <p className="text-sm mb-6" style={{ color: "var(--ab-text-muted)" }}>This saves your changes to the quote. The customer will <b>not</b> be emailed yet — use the <b>Email Update</b> button on the quote when you're ready to send.</p>
            <div className="flex gap-3">
              <button onClick={saveAndSend} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="confirm-done-yes">{busy ? <Loader2 size={14} className="animate-spin" /> : "Yes"}</button>
              <button onClick={() => setConfirmDone(false)} className="btn-secondary" data-testid="confirm-done-no">No</button>
            </div>
          </Modal>
        )}

        {/* Modal: Combine sibling quotes */}
        {combineOpen && (
          <Modal onClose={() => { if (!combining) setCombineOpen(false); }}>
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Combine with…</div>
            <h3 className="font-display text-2xl font-bold mb-3">Other open quotes from this customer</h3>
            <p className="text-sm mb-5" style={{ color: "var(--ab-text-muted)" }}>
              Pick one or more quotes to fold into <b>this</b> quote. Items from the picked
              quotes will be appended here and the originals will be deleted.
            </p>
            {siblingsLoading ? (
              <div className="py-6 flex justify-center"><Loader2 className="animate-spin" /></div>
            ) : siblings.length === 0 ? (
              <div className="py-4 text-sm" style={{ color: "var(--ab-text-dim)" }} data-testid="quote-combine-empty">
                No other open quotes from this customer.
              </div>
            ) : (
              <div className="space-y-2 mb-4 max-h-80 overflow-auto">
                {siblings.map((s) => {
                  const picked = pickedSiblings.has(s.id);
                  const valKeyS = quote.quote_type === "cash" ? "cash_value" : "trade_value";
                  const total = s.items.reduce((sum, it) => sum + (Number(it[valKeyS]) || 0), 0);
                  return (
                    <label
                      key={s.id}
                      className="flex items-center gap-3 p-3 cursor-pointer"
                      style={{ background: picked ? "rgba(37,99,235,0.10)" : "var(--ab-surface-2)", border: `1px solid ${picked ? "var(--ab-primary)" : "var(--ab-border)"}` }}
                      data-testid={`quote-combine-sibling-${s.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={picked}
                        onChange={() => togglePicked(s.id)}
                        data-testid={`quote-combine-checkbox-${s.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold">{s.items.length} item{s.items.length === 1 ? "" : "s"}</div>
                        <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>
                          {(s.status || "").toUpperCase()} · {new Date(s.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="font-mono text-sm" style={{ color: "var(--ab-primary)" }}>${total.toFixed(2)}</div>
                    </label>
                  );
                })}
              </div>
            )}
            {siblingErr && <div className="mb-3 text-sm" style={{ color: "var(--ab-danger)" }} data-testid="quote-combine-err">{siblingErr}</div>}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setCombineOpen(false)} disabled={combining} className="btn-secondary" data-testid="quote-combine-cancel">Cancel</button>
              <button
                onClick={confirmCombine}
                disabled={combining || pickedSiblings.size === 0}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="quote-combine-confirm"
              >
                {combining ? <Loader2 size={14} className="animate-spin" /> : <GitMerge size={14} />}
                Combine {pickedSiblings.size > 0 ? `(${pickedSiblings.size})` : ""}
              </button>
            </div>
          </Modal>
        )}

        {/* Modal: Have you issued payment? */}
        {confirmAccept && (
          <Modal onClose={() => setConfirmAccept(false)}>
            <h3 className="font-display text-2xl font-bold mb-3">Have you issued payment?</h3>
            <p className="text-sm mb-6" style={{ color: "var(--ab-text-muted)" }}>This will finalize the quote, add items to inventory, and move it to Accepted.</p>
            <div className="flex gap-3">
              <button onClick={accept} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="confirm-accept-yes">{busy ? <Loader2 size={14} className="animate-spin" /> : "Yes"}</button>
              <button onClick={() => setConfirmAccept(false)} className="btn-secondary" data-testid="confirm-accept-no">No</button>
            </div>
          </Modal>
        )}

        {/* Modal: Export PDF — Download or Email */}
        {exportOpen && (
          <Modal onClose={() => { if (!exporting) setExportOpen(false); }}>
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Export Quote</div>
            <h3 className="font-display text-2xl font-bold mb-3">Download or Email?</h3>
            <p className="text-sm mb-6" style={{ color: "var(--ab-text-muted)" }}>
              Download saves the PDF to this device. Email sends it as an attachment to the owner address on file.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={downloadPdf}
                disabled={!!exporting}
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="quote-export-download"
              >
                {exporting === "download" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download
              </button>
              <button
                onClick={emailPdf}
                disabled={!!exporting}
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="quote-export-email"
              >
                {exporting === "email" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email
              </button>
            </div>
            {exportMsg && (
              <div className="mt-5 px-3 py-2 text-xs" style={{ background: "var(--ab-surface-2)", border: "1px solid var(--ab-border)" }} data-testid="quote-export-msg">
                {exportMsg}
              </div>
            )}
            <div className="mt-5 flex justify-end">
              <button onClick={() => setExportOpen(false)} disabled={!!exporting} className="label-eyebrow" data-testid="quote-export-close">Close</button>
            </div>
          </Modal>
        )}
      </div>
    </Layout>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div className="surface p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}
